/**
 * Deciding whether a click inside a table row belongs to the row or to
 * something in it. Used by `SimpleTable`'s `onRowClick` (see there).
 *
 * Kept out of the component so it can be tested without a DOM: the decision
 * below is subtle enough to be worth pinning, and getting it wrong means either
 * a row that won't open or — worse — a row that opens when you click a menu
 * item in it.
 */

// Things inside a row that are already their own click target. A click on one
// of these is that control's, not the row's, which is what makes a clickable
// row safe on a table full of buttons, links, menus and checkboxes without
// wrapping each one in a stopPropagation handler.
//
// It can only recognise what the DOM tells it. Something interactive that
// renders as a plain element — a tag that filters the table when you click it,
// say — has to say so with `data-no-row-click`, which can go on the element or
// on any container around it.
export const OWN_CLICK_TARGET = [
  'a',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[contenteditable="true"]',
  '[data-no-row-click]'
].join(', ')

/**
 * Whether this click was aimed at a control inside the row rather than at the
 * row itself.
 */
export function startedOnItsOwnTarget (event) {
  const { target, currentTarget } = event
  if (!target?.closest) return false
  // React propagates events through the React tree, not the DOM tree, so a
  // click inside a portal — a menu, a popover, a modal opened from this row —
  // arrives here even though it is rendered somewhere else entirely. Its target
  // is not in the row's own DOM, and it belongs to whichever control in the row
  // opened it, never to the row.
  if (!currentTarget?.contains?.(target)) return true
  return !!target.closest(OWN_CLICK_TARGET)
}
